<template>
  <div class="commerce-section-wrapper">
    <!-- Commerce section main container -->
    <section class="card">
      <h2 class="name_section">Commerce</h2>
      <div class="body">

        <!-- Loading state -->
        <template v-if="musicReleaseCommerceLoader === true || loaderGetStripeAccount === true">
          <v-skeleton-loader
            type="image"
            class="skeleton_album_price"
          ></v-skeleton-loader>
        </template>

        <!-- Content when loaded -->
        <template v-if="musicReleaseCommerceLoader === false && loaderGetStripeAccount === false">
          <!-- Publish album free section -->
          <div class="publish_album_free" :class="{'active': publishFree}">
            <div class="material_checkbox">
              <input type="checkbox" id="publish-free" v-model="localPublishFree" required>
              <label for="publish-free">Publish this album as free for all under
                <a href="https://creativecommons.org/licenses/by-nc-sa/4.0/" target="_blank">Creative Commons License</a>
              </label>
            </div>

            <div class="alert_text" v-if="publishFree">
              Your album will be published free of charge and supporters will not be able to pay for it.
              To allow supporters to pay what they<br> want, set the Album Price to $0
            </div>
          </div>

          <!-- Stripe Account sections -->
          <div class="accept_payments" v-if="stripeAccount && stripeAccount.status !== 'complete'">
            <!-- No Account -->
            <template v-if="stripeAccount && stripeAccount.status === 'no-account'">
              <span>To accept payments</span>
              <button type="button" class="c2a_btn large" @click="createStripe">
                Get Stripe
                <span class="arrow">
                  <span class="line_svg"></span>
                  <svg width="7" height="10" viewBox="0 0 7 10" fill="none">
                    <path d="M1.5 1.5L5 5L1.5 8.5" stroke="white" stroke-width="2" stroke-linecap="square"/>
                  </svg>
                </span>
              </button>
            </template>

            <!-- Restricted -->
            <template v-if="stripeAccount && stripeAccount.status === 'restricted'">
              <div class="wrap_stripe_status">
                <div class="stripe_status restricted">
                  There is a problem with your Stripe account.
                  <button type="button" @click="resolveStripeIssue">Click here</button>
                  to resolve the issue
                </div>
              </div>
            </template>

            <!-- Pending -->
            <template v-if="stripeAccount && stripeAccount.status === 'pending'">
              <div class="stripe_status no-account">
                Your registration with Stripe is being reviewed. We will notify you when theres an update.
                <button type="button" @click="visitStripe" :class="{loading: loaderGetStripeAccountLink}">
                  Notification preferences
                  <v-progress-circular :width="2" indeterminate></v-progress-circular>
                </button>
              </div>
            </template>
          </div>

          <!-- Album price section (when not free) -->
          <div class="wrap_album_price" v-if="!localPublishFree && stripeAccount && stripeAccount.status === 'complete'">
            <!-- Album Price -->
            <div class="row_price">
              <div class="special_input" :class="{err: localAlbumPrice > 1000}">
                <span class="_label_">Album Price</span>
                <div class="_price_">
                  <span>$</span>
                  <input type="text" class="input_album_price" v-on:keypress="numbersOnly"
                        @keyup="albumPriceLimit" v-model="localAlbumPrice" v-price-format="localAlbumPrice">
                </div>
              </div>

              <label for="">Set the price $0 to let supporters pay what they want</label>
            </div>

            <!-- Select Allow Tracks -->
            <div class="allow_tracks">
              <div class="material_checkbox">
                <input type="checkbox" id="allow_tracks" v-model="localAllowTracksChecked" @change="changeAllowTracksChecked">
                <label for="allow_tracks">Allow tracks to be sold individually</label>
              </div>
            </div>

            <!-- Tracks List -->
            <div class="tracks_list" v-if="localAllowTracksChecked">
              <div v-for="(track, index) of musicReleaseCommerce.songs" :key="track.id" class="special_input item_track"
                  :class="{err: track.price > 100}">
                <span class="_label_">{{ track.title }}</span>
                <div class="_price_">
                  <span>$</span>
                  <input type="text" v-on:keypress="numbersOnly" @keyup="trackPriceLimit($event, index)" v-model="track.price"
                      @blur="onBlurTrackPrice(track, index)">
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </section>

    <!-- Navigation buttons -->
    <div class="step_actions">
      <button type="button" class="back tetriatary_btn" v-ripple @click="$emit('go-back')">Back</button>

      <template
        v-if="(localPublishFree && stripeAccount && stripeAccount.status !== 'complete') || ((localPublishFree || localAlbumPrice) && stripeAccount && stripeAccount.status === 'complete')">
        <button type="button" class="next primary_btn" v-ripple @click="saveCommerce('continue', 'default')"
              v-if="!createdAlbum.beenPublished">
          <span v-if="!createLoading">Next</span>
          <v-progress-circular v-if="createLoading" :width="2" indeterminate></v-progress-circular>
        </button>
        <button type="button" class="next primary_btn" v-ripple @click="saveCommerce('publish', 'default')"
              v-if="createdAlbum.beenPublished">
          <span v-if="!createLoading">Publish</span>
          <v-progress-circular v-if="createLoading" :width="2" indeterminate></v-progress-circular>
        </button>
      </template>
      <template v-else>
        <div class="next primary_btn disable">
          <template v-if="!createdAlbum.beenPublished">Next</template>
          <template v-if="createdAlbum.beenPublished">Publish</template>
        </div>
      </template>
    </div>

    <!-- DIALOGS -->
    <!-- Album price limit dialog -->
    <v-dialog
      v-if="albumPriceLimitDialog"
      v-model="albumPriceLimitDialog"
      max-width="570"
      :content-class="'price_limit_dialog'">
      <h2 class="header_dialog">
        Price limit
      </h2>
      <div class="cnt_dialog">
        If you'd like to price your release over $1,000<br> please contact support
      </div>
      <div class="wrap_actions_dialog">
        <button type="button" class="tetriatary_btn" @click="albumPriceLimitDialog = false" v-ripple>Cancel</button>
        <a v-if="facebook" :href="facebook" target="_blank" class="primary_btn" v-ripple>Contact Support</a>
      </div>
    </v-dialog>

    <!-- Track price limit dialog -->
    <v-dialog
      v-if="trackPriceLimitDialog"
      v-model="trackPriceLimitDialog"
      max-width="570"
      :content-class="'price_limit_dialog'">

      <h2 class="header_dialog">
        Price limit
      </h2>
      <div class="cnt_dialog">
        If you'd like to price your track over $100<br> please contact support
      </div>
      <div class="wrap_actions_dialog">
        <button type="button" class="tetriatary_btn" @click="trackPriceLimitDialog = false" v-ripple>Cancel</button>
        <a v-if="facebook" :href="facebook" target="_blank" class="primary_btn" v-ripple>Contact Support</a>
      </div>
    </v-dialog>

    <!-- User decides price dialog -->
    <v-dialog v-if="userDecidesPriceDialog"
              v-model="userDecidesPriceDialog"
              max-width="570"
              :content-class="'style3'">

      <h2 class="header_dialog">
        User Decides Price
      </h2>
      <div class="cnt_dialog">
        Leaving the price zero allows supporters to pay what they feel is fair during the transaction.
      </div>
      <div class="wrap_actions_dialog">
        <button type="button" class="tetriatary_btn" @click="userDecidesPriceDialog = false" v-ripple>Cancel</button>
        <button type="button" class="set_to_zero" @click="setToZero">Set to zero</button>
      </div>
    </v-dialog>

    <!-- Price distribution dialog -->
    <v-dialog v-if="priceDistributionDialog"
              v-model="priceDistributionDialog"
              max-width="570"
              :content-class="'style3'">

      <h2 class="header_dialog">
        Price distribution
      </h2>
      <div class="cnt_dialog">
        <div class="description">
          Divide the release price evenly among the tracks or choose one of the following options. Click cancel to set price manually.
        </div>

        <div class="select_distribution">
          <v-radio-group v-model="variantDistribution" column mandatory>
            <v-radio label="Round up to the nearest dollar: $1.30 → $2.00" value="ceil" color="#20C4F5" :ripple="false"></v-radio>
            <v-radio label="Set the price of tracks to zero to let users pay what they want" value="zero" color="#20C4F5"
                    :ripple="false"></v-radio>
          </v-radio-group>
        </div>
      </div>
      <div class="wrap_actions_dialog">
        <button type="button" class="cancel tetriatary_btn" @click="priceDistributionDialog = false" v-ripple>Cancel</button>
        <button type="button" class="apply" @click="distributionAlbumPrice(variantDistribution)" v-ripple>Apply</button>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'CommerceSection',
  props: {
    // Required props
    id: {
      type: [String, Number],
      required: true
    },
    // Data with two-way binding
    publishFree: {
      type: Boolean,
      default: false
    },
    albumPrice: {
      type: [String, Number],
      default: null
    },
    allowTracksChecked: {
      type: Boolean,
      default: false
    },
    // Store data passed down
    createdAlbum: {
      type: Object,
      required: true
    },
    musicReleaseCommerce: {
      type: Object,
      required: true
    },
    musicReleaseCommerceLoader: {
      type: Boolean,
      default: false
    },
    stripeAccount: {
      type: Object,
      default: null
    },
    loaderGetStripeAccount: {
      type: Boolean,
      default: false
    },
    loaderGetStripeAccountLink: {
      type: Boolean,
      default: false
    },
    createLinkStripeAccount: {
      type: String,
      default: ''
    },
    stripeAccountLink: {
      type: String,
      default: ''
    },
    facebook: {
      type: String,
      default: ''
    },
    createLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      validCommence: false,
      variantDistribution: '',
      // Dialog visibility flags
      albumPriceLimitDialog: false,
      trackPriceLimitDialog: false,
      userDecidesPriceDialog: false,
      priceDistributionDialog: false
    }
  },
  computed: {
    localPublishFree: {
      get() {
        return this.publishFree;
      },
      set(value) {
        this.$emit('update:publishFree', value);
      }
    },
    localAlbumPrice: {
      get() {
        return this.albumPrice;
      },
      set(value) {
        this.$emit('update:albumPrice', value);
      }
    },
    localAllowTracksChecked: {
      get() {
        return this.allowTracksChecked;
      },
      set(value) {
        this.$emit('update:allowTracksChecked', value);
      }
    }
  },
  mounted() {
    this.getStripeData();
  },
  methods: {
    ...mapActions([
      'GET_STRIPE_ACCOUNT',
      'GET_LINK_STRIPE_ACCOUNT',
      'CREATE_STRIPE_ACCOUNT',
      'POST_MUSIC_RELEASE_COMMERCE'
    ]),
    
    // Stripe data and account management
    getStripeData() {
      this.GET_STRIPE_ACCOUNT()
        .then(() => {
          if (this.stripeAccount && this.stripeAccount.status !== 'no-account') {
            this.GET_LINK_STRIPE_ACCOUNT()
              .catch(err => {
                console.error(`getStripeData, ${err}`);
              })
          }
        })
        .catch(err => {
          console.error(`getStripeAccount, ${err}`);
        })
    },
    
    createStripe() {
      let params = {
        callback_success_url: `${window.location.protocol}//${window.location.host}/create-music-release/${this.id}#step=3`,
      }
      this.CREATE_STRIPE_ACCOUNT(params)
        .then(() => {
          window.location.href = this.createLinkStripeAccount;
        })
        .catch(err => {
          console.error(`createStripe, ${err}`);
        })
        .finally(() => {
          this.getStripeData();
        })
    },
    
    resolveStripeIssue() {
      let params = {
        callback_success_url: `${window.location.protocol}//${window.location.host}/create-music-release/${this.id}#step=3`,
      }
      this.GET_LINK_STRIPE_ACCOUNT(params)
        .then(() => {
          window.location.href = this.stripeAccountLink;
        })
        .catch(err => {
          console.error(`getLinkStripeAccount, ${err}`);
        })
    },
    
    visitStripe() {
      window.location.href = this.stripeAccountLink;
    },
    
    // Price formatting and validation
    numbersOnly(event) {
      const charCode = (event.which) ? event.which : event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
        event.preventDefault();
      }
    },
    
    onBlurTrackPrice(value, index) {
      if (value.price !== null) {
        let newValue = String(value.price);
        if (newValue.length !== 0) {
          if (newValue.indexOf(".") === -1) {
            newValue = newValue + ".00";
          } else if (newValue.length - newValue.indexOf(".") - 1 < 2) {
            newValue = newValue + "0".repeat(2 - (newValue.length - newValue.indexOf(".") - 1));
          }
          this.musicReleaseCommerce.songs[index].price = parseFloat(newValue).toFixed(2);
        }
      }
    },
    
    albumPriceLimit(event) {
      const inputChar = event.key;
      if (inputChar !== '.' && isNaN(inputChar)) {
        event.preventDefault();
      } else {
        if (this.localAlbumPrice > 1000) {
          this.albumPriceLimitDialog = true;
        }
      }
    },
    
    trackPriceLimit(event, index) {
      const inputChar = event.key;
      if (inputChar !== '.' && isNaN(inputChar)) {
        event.preventDefault();
      } else {
        if (this.musicReleaseCommerce.songs[index].price > 100) {
          this.trackPriceLimitDialog = true;
        }
      }
    },
    
    // Price manipulation
    setToZero() {
      this.$emit('update:albumPrice', '0.00');
      this.distributionAlbumPrice('zero');
    },
    
    distributionAlbumPrice(variantDistribution) {
      let songs = this.musicReleaseCommerce.songs;
      let songsLength = songs.length;
      for (let i = 0; i < songsLength; i++) {
        let price = this.localAlbumPrice / songsLength;

        switch (variantDistribution) {
          case 'ceil':
            songs[i].price = Math.ceil(price).toFixed(2);
            break;
          case 'zero':
            songs[i].price = '0.00';
            break;
          case null:
            songs[i].price = null;
            break;
        }
      }
      if (this.priceDistributionDialog) {
        this.priceDistributionDialog = false;
      }
      if (this.userDecidesPriceDialog) {
        this.userDecidesPriceDialog = false;
      }
    },
    
    changeAllowTracksChecked() {
      if (this.localAllowTracksChecked === true && this.localAlbumPrice > 0) {
        this.priceDistributionDialog = true;
      }
    },
    
    // Save commerce settings
    async saveCommerce(save, afterSave) {
      this.validCommence = true;

      // Check publish free
      if (this.localPublishFree === true) {
        this.$emit('update:albumPrice', null);
        await this.distributionAlbumPrice('null');
      }

      if (this.localAllowTracksChecked === false) {
        await this.distributionAlbumPrice('null');
      }

      // Show userDecidesPriceDialog
      if (save === 'continue' && this.localPublishFree === false && !this.localAlbumPrice) {
        this.userDecidesPriceDialog = true;
        this.validCommence = false;
        return;
      }

      // Check empty tracks price
      let checkEmptyTracksPrice = this.musicReleaseCommerce.songs.find(track => (!track.price || false));
      if (save === 'continue' && this.localPublishFree === false && (+this.localAlbumPrice === 0 || +this.localAlbumPrice > 0) && (this.localAllowTracksChecked && checkEmptyTracksPrice)) {
        this.priceDistributionDialog = true;
        this.validCommence = false;
        return;
      }

      // Check max price
      let checkValidTracksPrice = this.musicReleaseCommerce.songs.find(track => track.price > 100);
      if (this.localPublishFree !== true && (!this.localAlbumPrice || this.localAlbumPrice > 1000 || (this.localAllowTracksChecked && checkValidTracksPrice))) {
        this.validCommence = false;
        return;
      }

      if (this.validCommence === true) {
        this.$emit('save-commerce-start');

        let params = {
          id: this.id,
          is_free: this.localPublishFree,
          price: this.localPublishFree !== true ? +this.localAlbumPrice : null
        }
        if (this.musicReleaseCommerce.songs && this.musicReleaseCommerce.songs.length) {
          let songs = [];
          for (let track of this.musicReleaseCommerce.songs) {
            let tracksPrice;
            if (this.localPublishFree || this.localAllowTracksChecked === false) {
              tracksPrice = null;
            }
            if (this.localAllowTracksChecked === true && !this.localPublishFree) {
              tracksPrice = +track.price;
            }
            
            songs.push({
              id: track.id,
              price: tracksPrice
            });
          }
          params.songs = songs;
        }

        this.POST_MUSIC_RELEASE_COMMERCE(params)
          .then(() => {
            this.$emit('save-commerce-complete', { save, afterSave });
          })
          .catch(err => {
            console.error(`POST_MUSIC_RELEASE_COMMERCE, ${err}`);
            this.$emit('save-commerce-error', err);
          });
      }
    }
  }
}
</script>

<style scoped>
/* Component specific styles can be added here if needed */
</style> 